import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, MapPin, Clock, Package } from "lucide-react";
import { format } from "date-fns";
import { useTenantTimezone } from "@/hooks/useTenantTimezone";
import { toast } from "sonner";

interface Tenant {
  id: string;
  name: string;
  slug: string;
  logo_url?: string;
  brand_primary?: string;
  feature_flags: {
    consumer_portal_enabled?: boolean;
  };
}

export default function ConsumerPortal() {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const { formatInTenantTimezone } = useTenantTimezone();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [address, setAddress] = useState("");
  const [condoName, setCondoName] = useState("");
  const [requestedDate, setRequestedDate] = useState<Date>();
  const [timeSlot, setTimeSlot] = useState("");
  const [items, setItems] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    loadTenant();
  }, [tenantSlug]);

  const loadTenant = async () => {
    if (!tenantSlug) return;
    
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, name, slug, logo_url, brand_primary, feature_flags')
        .eq('slug', tenantSlug)
        .single();

      if (error) throw error;

      const tenantData = {
        ...data,
        feature_flags: (data.feature_flags as any) || {}
      };
      setTenant(tenantData);
      
      if (!(tenantData.feature_flags as any)?.consumer_portal_enabled) {
        toast.error('Booking portal is not available for this organization');
      }
    } catch (error) {
      console.error('Error loading tenant:', error);
      toast.error('Organization not found');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!tenant || !(tenant.feature_flags as any)?.consumer_portal_enabled) return;

    setSubmitting(true);
    try {
      const orderData = {
        tenant_id: tenant.id,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        address,
        condo_name: condoName || null,
        requested_slot_start: requestedDate && timeSlot ? 
          new Date(`${format(requestedDate, 'yyyy-MM-dd')}T${timeSlot}:00`).toISOString() : null,
        requested_slot_end: requestedDate && timeSlot ? 
          new Date(`${format(requestedDate, 'yyyy-MM-dd')}T${timeSlot}:00`).toISOString() : null,
        notes,
        items_json: items.split('\n').filter(item => item.trim()).map(item => ({ description: item.trim() })),
        status: 'new' as const
      };

      const { error } = await supabase
        .from('consumer_orders')
        .insert(orderData);

      if (error) throw error;

      toast.success('Your booking request has been submitted successfully!');
      
      // Reset form
      setCustomerName("");
      setCustomerEmail("");
      setCustomerPhone("");
      setAddress("");
      setCondoName("");
      setRequestedDate(undefined);
      setTimeSlot("");
      setItems("");
      setNotes("");
      
    } catch (error) {
      console.error('Error submitting order:', error);
      toast.error('Failed to submit booking request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!tenant) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <h1 className="text-2xl font-bold mb-2">Organization Not Found</h1>
            <p className="text-muted-foreground">
              The requested organization could not be found.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!(tenant.feature_flags as any)?.consumer_portal_enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="text-center py-8">
            <h1 className="text-2xl font-bold mb-2">Booking Unavailable</h1>
            <p className="text-muted-foreground">
              Online booking is not currently available for {tenant.name}.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background to-muted/50">
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="text-center mb-8">
            {tenant.logo_url && (
              <img 
                src={tenant.logo_url} 
                alt={tenant.name} 
                className="h-16 mx-auto mb-4"
              />
            )}
            <h1 className="text-3xl font-bold mb-2">{tenant.name}</h1>
            <p className="text-muted-foreground">Book your delivery or service</p>
          </div>

          {/* Booking Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Service Request
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Customer Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Contact Information</h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="name">Full Name *</Label>
                      <Input
                        id="name"
                        value={customerName}
                        onChange={(e) => setCustomerName(e.target.value)}
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="phone">Phone Number *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={customerPhone}
                        onChange={(e) => setCustomerPhone(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      required
                    />
                  </div>
                </div>

                {/* Location Information */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <MapPin className="h-5 w-5" />
                    Location Details
                  </h3>
                  
                  <div>
                    <Label htmlFor="address">Full Address *</Label>
                    <Textarea
                      id="address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      placeholder="Street address, unit number, postal code"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="condo">Building/Condo Name (if applicable)</Label>
                    <Input
                      id="condo"
                      value={condoName}
                      onChange={(e) => setCondoName(e.target.value)}
                    />
                  </div>
                </div>

                {/* Scheduling */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    Preferred Schedule
                  </h3>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>Preferred Date</Label>
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" className="w-full justify-start">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {requestedDate ? formatInTenantTimezone(requestedDate, 'PPP') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={requestedDate}
                            onSelect={setRequestedDate}
                            disabled={(date) => date < new Date()}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                    
                    <div>
                      <Label htmlFor="time">Preferred Time</Label>
                      <Input
                        id="time"
                        type="time"
                        value={timeSlot}
                        onChange={(e) => setTimeSlot(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Service Details */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Service Details</h3>
                  
                  <div>
                    <Label htmlFor="items">Items/Services Required</Label>
                    <Textarea
                      id="items"
                      value={items}
                      onChange={(e) => setItems(e.target.value)}
                      placeholder="List each item or service on a new line"
                      rows={4}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="notes">Special Instructions</Label>
                    <Textarea
                      id="notes"
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      placeholder="Any special instructions or notes for the service provider"
                      rows={3}
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={submitting}
                  style={{ backgroundColor: tenant.brand_primary || undefined }}
                >
                  {submitting ? 'Submitting...' : 'Submit Request'}
                </Button>
              </form>
            </CardContent>
          </Card>

          <div className="text-center mt-6 text-sm text-muted-foreground">
            <p>We'll contact you within 24 hours to confirm your request.</p>
          </div>
        </div>
      </div>
    </div>
  );
}